import React from "react";
import PropTypes from "prop-types";

import useSheetStack from "../common/useSheetStack";
import SheetStack from "../common/SheetStack";
import { HOSP_V2_ADD, HOSP_V2_EDIT } from "./events";
import HospitalisationForm from "./HospitalisationForm";

/**
 * Hospitalisation editor rail — a STACK of write sheets, newest on top.
 *
 * All of the stack behaviour (the blank-sheet guard, one-sheet-per-record,
 * serialised saves, the refusal pulse, the live region) lives in
 * ../common/useSheetStack + ../common/SheetStack, which every v3 rail shares.
 * This file is now only the section's wiring: which events open a sheet, how
 * to pull a hospitalisation out of an edit payload, and what to call each
 * sheet. The form itself — cause, status, the two-way stepper/date sync, the
 * procedure chips and the write — is HospitalisationForm.
 *
 * The rail owns BOTH flows: ADD (toolbar / empty state → HOSP_V2_ADD) and
 * UPDATE (row cause or status chip → HOSP_V2_EDIT, carrying
 * `{ hospitalisation }`). A bare HOSP_V2_EDIT payload opens a blank add, the
 * same contract as LI_V2_EDIT — useSheetStack handles that fallback when
 * getRecord returns nothing.
 */

const ADD = [HOSP_V2_ADD];
const EDIT = [HOSP_V2_EDIT];

const getRecord = (payload) => payload && payload.hospitalisation;
const getRecordId = (h) => h && h.hospitalisationId;
const causeOf = (h) => (h && h.cause && h.cause.icdName) || "This entry";
const dupMessage = (h) => `${causeOf(h)} is already open for update.`;

const HospitalisationEditor = ({ patient }) => {
  const patientId = patient && (patient.id || patient.patientID);

  const stack = useSheetStack({
    sectionKey: "hospitalisation",
    addEvents: ADD,
    editEvents: EDIT,
    getRecord,
    getRecordId,
    blankMessage: "A blank hospitalisation form is already open.",
    dupMessage,
    enabled: !!patientId,
  });

  if (!patientId) {
    return <div className="ph-slot-empty is-editor"><p>Waiting for patient…</p></div>;
  }

  return (
    <SheetStack
      stack={stack}
      className="cc3-editor"
      describe={(s) =>
        s.editing ? `Update ${causeOf(s.editing)}` : "Add hospitalisation"
      }
    >
      {(sheet, props) => <HospitalisationForm patientId={patientId} {...props} />}
    </SheetStack>
  );
};

HospitalisationEditor.propTypes = {
  patient: PropTypes.object,
};

export default HospitalisationEditor;
